import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MessageSquare,
  X,
  Send,
  Image as ImageIcon,
  Loader2,
  Trash2,
  Plus,
  ChevronLeft,
  Sparkles,
  Bookmark,
  Edit3,
  Check,
  Upload,
  Copy,
  CheckCheck,
  Maximize2,
  Minimize2,
  Mic,
  MicOff,
  RotateCcw,
  Square,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { useDashboardTheme } from "@/hooks/useDashboardTheme";
import useSpeechRecognition from "@/hooks/useSpeechRecognition";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AIAssistant({ token, isDark: isDarkProp, fullPage = false }) {
  // Use prop if provided, otherwise fall back to hook
  const hookTheme = useDashboardTheme();
  const isDark = isDarkProp !== undefined ? isDarkProp : hookTheme.isDark;

  // Theme tokens
  const t = isDark ? {
    panel: "bg-[#0F1A2E] border-white/10",
    header: "bg-[#0D1525] border-b border-white/10",
    text: "text-white",
    textMuted: "text-white/60",
    textDim: "text-white/40",
    textFaint: "text-white/30",
    card: "bg-white/5 border border-white/10",
    cardHover: "hover:bg-white/10",
    input: "bg-white/5 border-white/10 text-white placeholder:text-white/30",
    userBubble: "bg-emerald-600 text-white",
    aiBubble: "bg-white/10 text-white/90",
    aiProse: "prose-invert [&_strong]:text-emerald-300",
    iconBtn: "text-white/50 hover:text-white hover:bg-white/5",
    dragOverlay: "bg-emerald-600/20 border-emerald-400",
  } : {
    panel: "bg-white border-gray-200",
    header: "bg-gray-50 border-b border-gray-200",
    text: "text-gray-900",
    textMuted: "text-gray-600",
    textDim: "text-gray-400",
    textFaint: "text-gray-300",
    card: "bg-gray-50 border border-gray-200",
    cardHover: "hover:bg-gray-100",
    input: "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400",
    userBubble: "bg-emerald-600 text-white",
    aiBubble: "bg-gray-100 text-gray-800",
    aiProse: "[&_strong]:text-emerald-700",
    iconBtn: "text-gray-400 hover:text-gray-700 hover:bg-gray-100",
    dragOverlay: "bg-emerald-50 border-emerald-400",
  };
  const [isOpen, setIsOpen] = useState(fullPage);
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);
  const [pendingImages, setPendingImages] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showPrompts, setShowPrompts] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);

  // Voice input
  const speech = useSpeechRecognition({
    onResult: (transcript) => setInput((prev) => prev ? prev + " " + transcript : transcript),
  });

  // Saved prompts state
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [isAddingPrompt, setIsAddingPrompt] = useState(false);
  const [newPromptLabel, setNewPromptLabel] = useState("");
  const [newPromptText, setNewPromptText] = useState("");
  const [editingPromptId, setEditingPromptId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [editText, setEditText] = useState("");

  // Drag-and-drop state
  const [isDragging, setIsDragging] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [isExpanded, setIsExpanded] = useState(fullPage);
  const [uploadingCount, setUploadingCount] = useState(0);
  const dragCounter = useRef(0);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      }
    });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Also scroll when last message text changes (streaming deltas)
  const lastMsgText = messages.length > 0 ? messages[messages.length - 1]?.text : "";
  useEffect(() => {
    if (lastMsgText) scrollToBottom();
  }, [lastMsgText, scrollToBottom]);

  // Scroll when panel opens or re-opens
  useEffect(() => {
    if (isOpen && messages.length > 0) {
      // Double rAF to wait for panel mount + layout
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottom();
        });
      });
    }
  }, [isOpen, scrollToBottom]); // eslint-disable-line

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/ai/conversations`, { headers });
      setConversations(data);
    } catch { /* silent */ }
  }, [token]);  

  // Load saved prompts
  const loadPrompts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/ai/prompts`, { headers });
      setSavedPrompts(data);
    } catch { /* silent */ }
  }, [token]);  

  useEffect(() => {
    if (isOpen) {
      // Reset view states so the input is always visible on reopen
      setShowHistory(false);
      setShowPrompts(false);
      loadConversations();
      loadPrompts();
    }
  }, [isOpen, loadConversations, loadPrompts]);

  const openConversation = useCallback(async (convId) => {
    setActiveConvId(convId);
    setShowHistory(false);
    setShowPrompts(false);
    setIsLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/ai/conversations/${convId}`, { headers });
      setMessages(data.messages || []);
    } catch {
      toast.error("Failed to load conversation");
    } finally {
      setIsLoading(false);
      requestAnimationFrame(() => requestAnimationFrame(() => scrollToBottom()));
    }
  }, [token]);  

  const startNewChat = useCallback(async () => {
    try {
      const { data } = await axios.post(`${API}/api/ai/conversations`, { title: null }, { headers });
      setActiveConvId(data.id);
      setMessages([]);
      setShowHistory(false);
      setShowPrompts(false);
      loadConversations();
    } catch {
      toast.error("Failed to start new chat");
    }
  }, [token, loadConversations]);  

  const deleteConversation = async (convId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/ai/conversations/${convId}`, { headers });
      if (activeConvId === convId) { setActiveConvId(null); setMessages([]); }
      loadConversations();
    } catch { toast.error("Failed to delete"); }
  };

  // --- Image handling (file picker + drag-and-drop) ---

  // Compress image on client before upload to avoid proxy size limits
  const compressImage = (file, maxWidth = 1600, quality = 0.8) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement("canvas");
          let w = img.width;
          let h = img.height;
          if (w > maxWidth) {
            h = Math.round((h * maxWidth) / w);
            w = maxWidth;
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d").drawImage(img, 0, 0, w, h);
          canvas.toBlob(
            (blob) => {
              if (!blob || blob.size < 100) {
                // toBlob failed (can happen on mobile with memory pressure)
                console.warn("Image compression returned empty blob, using original");
                resolve(file);
                return;
              }
              const compressed = new File([blob], file.name, { type: "image/jpeg" });
              resolve(compressed);
            },
            "image/jpeg",
            quality
          );
        } catch (e) {
          console.warn("Image compression error:", e);
          resolve(file);
        }
      };
      img.onerror = () => resolve(file); // fallback to original
      img.src = URL.createObjectURL(file);
    });
  };

  const processFiles = async (files) => {
    const validFiles = [];
    for (const file of files) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
        toast.error(`${file.name}: Only JPEG, PNG, WEBP allowed`);
        continue;
      }
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name}: Must be under 20MB`);
        continue;
      }
      validFiles.push(file);
    }
    if (!validFiles.length) return;

    setUploadingCount(validFiles.length);

    // Compress and upload all files
    const uploads = validFiles.map(async (file) => {
      try {
        // Compress large images to fit within proxy limits
        let uploadFile = file;
        if (file.size > 800 * 1024) {
          // Use more aggressive compression for very large files (>5MB)
          const quality = file.size > 5 * 1024 * 1024 ? 0.6 : 0.8;
          const maxWidth = file.size > 5 * 1024 * 1024 ? 1200 : 1600;
          uploadFile = await compressImage(file, maxWidth, quality);
        }

        const formData = new FormData();
        formData.append("file", uploadFile);
        const { data } = await axios.post(`${API}/api/ai/upload-image`, formData, {
          headers: { ...headers, "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        return { id: data.id, name: file.name, preview: URL.createObjectURL(file) };
      } catch (err) {
        const msg = err.response?.data?.detail || err.message || "Upload failed";
        toast.error(`${file.name}: ${msg}`);
        return null;
      }
    });

    const results = await Promise.all(uploads);
    const successful = results.filter(Boolean);
    if (successful.length) {
      setPendingImages((prev) => [...prev, ...successful]);
      if (successful.length > 1) toast.success(`${successful.length} images uploaded`);
    }
    setUploadingCount(0);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    await processFiles(files);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Drag-and-drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes("Files")) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounter.current = 0;
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) await processFiles(files);
  };

  const removePendingImage = (imgId) => {
    setPendingImages((prev) => {
      const img = prev.find((i) => i.id === imgId);
      if (img?.preview) URL.revokeObjectURL(img.preview);
      return prev.filter((i) => i.id !== imgId);
    });
  };

  // --- Send message ---
  const sendMessage = async (overrideText = null, overrideImageIds = null) => {
    const text = overrideText ?? input.trim();
    const imageIds = overrideImageIds ?? pendingImages.map((i) => i.id);
    if (!text && imageIds.length === 0) return;
    if (isStreaming) return;

    let convId = activeConvId;
    if (!convId) {
      try {
        const { data } = await axios.post(`${API}/api/ai/conversations`, { title: null }, { headers });
        convId = data.id;
        setActiveConvId(convId);
      } catch {
        toast.error("Failed to start conversation");
        return;
      }
    }

    const userMsg = {
      role: "user",
      text: text || "(image attached)",
      image_ids: imageIds,
      _previews: overrideText ? [] : pendingImages.map((i) => i.preview),
      timestamp: new Date().toISOString(),
    };

    if (!overrideText) {
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setPendingImages([]);
    }
    setIsStreaming(true);
    setMessages((prev) => [...prev, { role: "assistant", text: "", timestamp: new Date().toISOString() }]);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const response = await fetch(`${API}/api/ai/conversations/${convId}/messages`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ text: text || "Describe this image and suggest a Vendoo listing.", image_ids: imageIds }),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errBody = await response.text().catch(() => "");
        throw new Error(errBody || `Server error (${response.status})`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const payload = JSON.parse(line.slice(6));
            if (payload.type === "delta") {
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") {
                  updated[updated.length - 1] = { ...last, text: last.text + payload.content };
                }
                return updated;
              });
              scrollToBottom();
            } else if (payload.type === "error") {
              // Backend reported an AI processing error
              console.error("AI error:", payload.content);
              setMessages((prev) => {
                const updated = [...prev];
                const last = updated[updated.length - 1];
                if (last?.role === "assistant") {
                  updated[updated.length - 1] = { ...last, text: "Sorry, I had trouble processing that. Please try again." };
                }
                return updated;
              });
            } else if (payload.type === "done") {
              // Stream finished successfully
              if (!payload.content) {
                setMessages((prev) => {
                  const updated = [...prev];
                  const last = updated[updated.length - 1];
                  if (last?.role === "assistant" && !last.text) {
                    updated[updated.length - 1] = { ...last, text: "I couldn't generate a response. Please try again." };
                  }
                  return updated;
                });
              }
            }
          } catch { /* partial chunk */ }
        }
      }
      loadConversations();
    } catch (err) {
      if (err.name === "AbortError") {
        // User cancelled — keep whatever partial response was received
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant" && !last.text) {
            updated[updated.length - 1] = { ...last, text: "(Stopped)" };
          }
          return updated;
        });
      } else {
        toast.error("Failed to get response");
        setMessages((prev) => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.role === "assistant" && !last.text) {
            updated[updated.length - 1] = { ...last, text: "Sorry, something went wrong. Please try again." };
          }
          return updated;
        });
      }
    } finally {
      abortControllerRef.current = null;
      setIsStreaming(false);
    }
  };

  const stopStreaming = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  };

  const regenerateLastMessage = () => {
    // Find the last user message and resend it
    const lastUserIdx = [...messages].reverse().findIndex((m) => m.role === "user");
    if (lastUserIdx === -1) return;
    const idx = messages.length - 1 - lastUserIdx;
    const lastUserMsg = messages[idx];

    // Remove last user + assistant pair from UI
    setMessages((prev) => prev.slice(0, idx));

    // Resend
    sendMessage(lastUserMsg.text, lastUserMsg.image_ids || []);
  };

  const deleteLastExchange = () => {
    // Remove the last user+assistant pair from the UI
    const lastUserIdx = [...messages].reverse().findIndex((m) => m.role === "user");
    if (lastUserIdx === -1) return;
    const idx = messages.length - 1 - lastUserIdx;
    setMessages((prev) => prev.slice(0, idx));
    toast.success("Last exchange removed");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // --- Saved prompts CRUD ---
  const saveNewPrompt = async () => {
    if (!newPromptLabel.trim() || !newPromptText.trim()) {
      toast.error("Both name and prompt text are required");
      return;
    }
    try {
      await axios.post(`${API}/api/ai/prompts`, {
        label: newPromptLabel.trim(),
        text: newPromptText.trim(),
      }, { headers });
      setNewPromptLabel("");
      setNewPromptText("");
      setIsAddingPrompt(false);
      loadPrompts();
      toast.success("Prompt saved!");
    } catch {
      toast.error("Failed to save prompt");
    }
  };

  const updatePrompt = async (promptId) => {
    try {
      await axios.put(`${API}/api/ai/prompts/${promptId}`, {
        label: editLabel.trim() || undefined,
        text: editText.trim() || undefined,
      }, { headers });
      setEditingPromptId(null);
      loadPrompts();
    } catch {
      toast.error("Failed to update");
    }
  };

  const deletePrompt = async (promptId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/api/ai/prompts/${promptId}`, { headers });
      loadPrompts();
    } catch {
      toast.error("Failed to delete prompt");
    }
  };

  const applyPrompt = (promptText) => {
    setInput(promptText);
    setShowPrompts(false);
    inputRef.current?.focus();
  };

  // Copy assistant message text
  const copyMessage = (text, idx) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIdx(idx);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedIdx(null), 2000);
    }).catch(() => toast.error("Failed to copy"));
  };

  // --- Render helpers ---
  const renderMessage = (msg, idx) => {
    const isUser = msg.role === "user";
    // Use local previews if available, otherwise build API URLs from stored image_ids
    const imageSrcs = msg._previews?.length > 0
      ? msg._previews
      : (msg.image_ids || []).map((id) => `${API}/api/ai/images/${id}?token=${token}`);

    return (
      <div key={idx} className={`flex ${isUser ? "justify-end" : "justify-start"} mb-3 group/msg`} data-testid={`chat-message-${idx}`}>
        <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${isUser ? t.userBubble + " rounded-br-md" : t.aiBubble + " rounded-bl-md"}`}>
          {isUser && imageSrcs.length > 0 && (
            <div className="flex gap-1.5 mb-2 flex-wrap">
              {imageSrcs.map((src, i) => (
                <img key={i} src={src} alt="attached" onClick={() => setLightboxSrc(src)} className="w-10 h-10 object-cover rounded-lg border border-white/20 cursor-pointer hover:opacity-80 transition-opacity" />
              ))}
            </div>
          )}
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
          ) : (
            <div className={`text-sm prose prose-sm max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_h3]:mt-2 [&_h3]:mb-1 ${t.aiProse}`}>
              {msg.text ? <ReactMarkdown>{msg.text}</ReactMarkdown> : (
                <span className={`inline-flex items-center gap-1 ${t.textDim}`}><Loader2 className="w-3 h-3 animate-spin" /> Thinking...</span>
              )}
            </div>
          )}
          <div className="flex items-center justify-between mt-1">
            <p className="text-[10px] opacity-40">
              {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ""}
            </p>
            {!isUser && msg.text && (
              <button
                onClick={() => copyMessage(msg.text, idx)}
                className="sm:opacity-0 sm:group-hover/msg:opacity-100 transition-opacity p-1 -mr-1 rounded hover:bg-white/10"
                title="Copy to clipboard"
                data-testid={`copy-message-${idx}`}
              >
                {copiedIdx === idx
                  ? <CheckCheck className="w-3.5 h-3.5 text-emerald-400" />
                  : <Copy className={`w-3.5 h-3.5 ${t.textDim}`} />
                }
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Active view — show input when not in prompts manager.
  // In fullPage/expanded mode, history is in sidebar so input should still show.
  // In compact mode, history replaces the main area so input hides.
  const isActiveChat = !showPrompts && (!showHistory || isExpanded || fullPage);

  // ─── Full-page rendering (admin dashboard) ───
  if (fullPage) {
    return (
      <div
        className="flex flex-col h-[calc(100vh-120px)] overflow-hidden"
        data-testid="ai-assistant-panel"
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {/* Header */}
        <div className={`flex items-center justify-between px-4 py-3 shrink-0 border-b ${isDark ? "border-white/10" : "border-gray-200"}`}>
          <div className="flex items-center gap-2">
            {(activeConvId || showHistory || showPrompts) && (
              <button
                onClick={() => {
                  if (showHistory || showPrompts) { setShowHistory(false); setShowPrompts(false); }
                  else { setActiveConvId(null); setMessages([]); }
                }}
                className={`${t.textMuted} p-1`}
                data-testid="ai-back-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <Sparkles className="w-5 h-5 text-emerald-400" />
            <span className={`${t.text} font-semibold`}>
              {showHistory ? "Chat History" : showPrompts ? "Saved Prompts" : "Listing Assistant"}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { setShowPrompts((v) => !v); setShowHistory(false); }}
              className={`p-2 rounded-lg transition-colors ${showPrompts ? "text-emerald-400 bg-emerald-400/10" : t.iconBtn}`}
              title="Saved prompts" data-testid="ai-prompts-btn"
            >
              <Bookmark className="w-4 h-4" />
            </button>
            <button
              onClick={() => { setShowHistory((v) => !v); setShowPrompts(false); }}
              className={`sm:hidden p-2 rounded-lg transition-colors ${showHistory ? "text-emerald-400 bg-emerald-400/10" : t.iconBtn}`}
              title="Chat history" data-testid="ai-history-btn"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            <button onClick={startNewChat} className={`${t.iconBtn} p-2 rounded-lg`} title="New chat" data-testid="ai-new-chat-btn">
              <Plus className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body — sidebar + chat */}
        <div className="flex-1 flex flex-row overflow-hidden min-h-0">
          {/* Sidebar — desktop only */}
          <div className={`hidden sm:flex sm:flex-col w-[220px] shrink-0 border-r ${isDark ? "border-white/10" : "border-gray-200"} overflow-y-auto`} data-testid="ai-sidebar">
            <div className="p-3 flex-1">
              <div className="flex items-center justify-between mb-3">
                <p className={`${t.textMuted} text-xs font-semibold uppercase tracking-wider`}>Recent</p>
                <button onClick={startNewChat} className={`${t.iconBtn} p-1 rounded`} title="New chat" data-testid="sidebar-new-chat">
                  <Plus className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="space-y-1">
                {conversations.length === 0 ? (
                  <p className={`${t.textDim} text-xs text-center py-4`}>No chats yet</p>
                ) : conversations.map((c) => (
                  <div
                    key={c.id}
                    onClick={() => openConversation(c.id)}
                    className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors group ${
                      activeConvId === c.id
                        ? isDark ? "bg-emerald-600/20 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                        : t.cardHover
                    }`}
                    data-testid={`sidebar-conv-${c.id}`}
                  >
                    <MessageSquare className={`w-3.5 h-3.5 shrink-0 ${activeConvId === c.id ? "" : t.textDim}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs font-medium truncate ${activeConvId === c.id ? "" : t.text}`}>{c.title || "Untitled chat"}</p>
                    </div>
                    <button
                      onClick={(e) => deleteConversation(c.id, e)}
                      className={`${t.textFaint} hover:text-red-400 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0`}
                      data-testid={`sidebar-delete-${c.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              {conversations.length > 0 && (
                <button
                  onClick={async () => {
                    if (!window.confirm("Delete all conversations?")) return;
                    for (const c of conversations) {
                      try { await axios.delete(`${API}/api/ai/conversations/${c.id}`, { headers }); } catch {}
                    }
                    setActiveConvId(null);
                    setMessages([]);
                    loadConversations();
                    toast.success("All chats cleared");
                  }}
                  className="w-full text-center py-2 mt-3 text-[10px] text-red-400/60 hover:text-red-400 transition-colors"
                  data-testid="sidebar-clear-all"
                >
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Main chat area */}
          <div className="flex-1 flex flex-col overflow-hidden min-h-0">
            {/* Scrollable content */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 relative">
              {/* Drag overlay */}
              {isDragging && (
                <div className={`absolute inset-0 z-10 ${t.dragOverlay} border-2 border-dashed rounded-xl flex flex-col items-center justify-center backdrop-blur-sm`}>
                  <Upload className="w-10 h-10 text-emerald-400 mb-2" />
                  <p className="text-emerald-300 font-medium text-sm">Drop images here</p>
                  <p className="text-emerald-300/60 text-xs mt-1">JPEG, PNG, WEBP up to 5MB</p>
                </div>
              )}

              {showPrompts ? (
                /* Saved Prompts Manager */
                <div className="space-y-3 max-w-2xl mx-auto">
                  {isAddingPrompt ? (
                    <div className={`p-3 rounded-xl ${t.card} space-y-2`}>
                      <input value={newPromptLabel} onChange={(e) => setNewPromptLabel(e.target.value)} placeholder="Prompt name (e.g. 'Vintage Denim')" className={`w-full ${t.input} rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500/40`} data-testid="new-prompt-label-input" />
                      <textarea value={newPromptText} onChange={(e) => setNewPromptText(e.target.value)} placeholder="Prompt text that will be sent to the assistant..." rows={3} className={`w-full ${t.input} rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-500/40`} data-testid="new-prompt-text-input" />
                      <div className="flex gap-2 justify-end">
                        <button onClick={() => { setIsAddingPrompt(false); setNewPromptLabel(""); setNewPromptText(""); }} className={`px-3 py-1.5 rounded-lg ${t.textDim} text-xs ${t.cardHover}`}>Cancel</button>
                        <button onClick={saveNewPrompt} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs hover:bg-emerald-500" data-testid="save-new-prompt-btn">Save</button>
                      </div>
                    </div>
                  ) : (
                    <button onClick={() => setIsAddingPrompt(true)} className={`w-full p-3 rounded-xl border border-dashed ${isDark ? "border-white/20 text-white/50" : "border-gray-300 text-gray-400"} text-sm hover:border-emerald-500/40 hover:text-emerald-300 transition-colors flex items-center justify-center gap-2`} data-testid="add-prompt-btn">
                      <Plus className="w-4 h-4" /> Create New Prompt
                    </button>
                  )}
                  {savedPrompts.length === 0 && !isAddingPrompt && (
                    <p className={`${t.textFaint} text-sm text-center mt-6`}>No saved prompts yet. Create one to use it with a single tap.</p>
                  )}
                  {savedPrompts.map((p) => (
                    <div key={p.id} className={`p-3 rounded-xl ${t.card} ${t.cardHover} transition-colors group`} data-testid={`prompt-item-${p.id}`}>
                      {editingPromptId === p.id ? (
                        <div className="space-y-2">
                          <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className={`w-full ${t.input} rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-500/40`} data-testid="edit-prompt-label-input" />
                          <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} className={`w-full ${t.input} rounded-lg px-3 py-1.5 text-sm resize-none focus:outline-none focus:border-emerald-500/40`} data-testid="edit-prompt-text-input" />
                          <div className="flex gap-2 justify-end">
                            <button onClick={() => setEditingPromptId(null)} className={`px-2 py-1 ${t.textDim} text-xs ${t.cardHover} rounded`}>Cancel</button>
                            <button onClick={() => updatePrompt(p.id)} className="px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500" data-testid="confirm-edit-prompt-btn"><Check className="w-3 h-3" /></button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between gap-2">
                          <button onClick={() => applyPrompt(p.text)} className="text-left flex-1 min-w-0">
                            <p className={`${t.text} font-medium text-sm truncate`}>{p.label}</p>
                            <p className={`${t.textDim} text-xs mt-0.5 line-clamp-2`}>{p.text}</p>
                          </button>
                          <div className="flex items-center gap-0.5 shrink-0">
                            <button onClick={() => { setEditingPromptId(p.id); setEditLabel(p.label); setEditText(p.text); }} className={`p-1 ${t.textFaint} hover:${t.text}`} data-testid={`edit-prompt-${p.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                            <button onClick={(e) => deletePrompt(p.id, e)} className={`p-1 ${t.textFaint} hover:text-red-400`} data-testid={`delete-prompt-${p.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : showHistory ? (
                /* History list — mobile only (desktop uses sidebar) */
                <div className="space-y-2 max-w-2xl mx-auto">
                  {conversations.length > 0 && (
                    <button
                      onClick={async () => {
                        if (!window.confirm("Delete all conversations?")) return;
                        for (const c of conversations) {
                          try { await axios.delete(`${API}/api/ai/conversations/${c.id}`, { headers }); } catch {}
                        }
                        setActiveConvId(null); setMessages([]); loadConversations();
                        toast.success("All chats cleared");
                      }}
                      className="w-full text-center py-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
                      data-testid="clear-all-chats-btn"
                    >Clear all chats</button>
                  )}
                  {conversations.length === 0 ? (
                    <p className={`${t.textDim} text-sm text-center mt-8`}>No conversations yet</p>
                  ) : conversations.map((c) => (
                    <div key={c.id} onClick={() => openConversation(c.id)} className={`flex items-center justify-between p-3 rounded-xl ${t.card} ${t.cardHover} cursor-pointer transition-colors group`} data-testid={`conv-item-${c.id}`}>
                      <div className="min-w-0 flex-1">
                        <p className={`${t.text} text-sm font-medium truncate`}>{c.title || "Untitled chat"}</p>
                        <p className={`${t.textDim} text-xs mt-0.5`}>{new Date(c.updated_at).toLocaleDateString()}</p>
                      </div>
                      <button onClick={(e) => deleteConversation(c.id, e)} className={`${t.textFaint} hover:text-red-400 p-1 transition-opacity`} data-testid={`conv-delete-${c.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
                </div>
              ) : messages.length === 0 && !activeConvId ? (
                /* Welcome */
                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                  <div className="w-14 h-14 rounded-full bg-emerald-600/20 flex items-center justify-center mb-4">
                    <Sparkles className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h3 className={`${t.text} font-semibold text-base mb-1`}>Listing Assistant</h3>
                  <p className={`${t.textDim} text-sm mb-5 max-w-[300px]`}>
                    Drop a screenshot or describe an item. I'll write a Vendoo listing for you.
                  </p>
                  {savedPrompts.length > 0 && (
                    <div className="w-full max-w-[320px] mb-4">
                      <p className={`${t.textFaint} text-xs uppercase tracking-wider mb-2`}>Your prompts</p>
                      <div className="flex flex-wrap gap-2 justify-center">
                        {savedPrompts.slice(0, 6).map((p) => (
                          <button key={p.id} onClick={() => applyPrompt(p.text)} className={`px-3 py-1.5 rounded-full ${t.card} ${isDark ? "text-white/70" : "text-gray-600"} text-xs hover:bg-emerald-600/20 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors truncate max-w-[140px]`} data-testid={`welcome-prompt-${p.id}`}>
                            {p.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className={`w-full max-w-[300px] p-4 rounded-xl border border-dashed ${isDark ? "border-white/15 text-white/30" : "border-gray-300 text-gray-400"} text-xs flex flex-col items-center gap-1.5`}>
                    <Upload className={`w-5 h-5 ${isDark ? "text-white/20" : "text-gray-300"}`} />
                    Drag & drop screenshots here
                  </div>
                </div>
              ) : (
                <div className="max-w-3xl mx-auto">
                  {messages.map(renderMessage)}
                  {!isStreaming && messages.length >= 2 && messages[messages.length - 1]?.role === "assistant" && (
                    <div className="flex items-center gap-2 justify-center py-1">
                      <button onClick={regenerateLastMessage} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${t.textDim} ${t.cardHover} transition-colors`} title="Regenerate response" data-testid="ai-regenerate-btn">
                        <RotateCcw className="w-3.5 h-3.5" /> Retry
                      </button>
                      <button onClick={deleteLastExchange} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${t.textDim} hover:text-red-400 transition-colors`} title="Delete last exchange" data-testid="ai-delete-last-btn">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Pending images strip */}
            {(pendingImages.length > 0 || uploadingCount > 0) && (
              <div className={`px-3 sm:px-6 py-2 border-t ${isDark ? "border-white/5" : "border-gray-200"} shrink-0`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <ImageIcon className={`w-3.5 h-3.5 ${t.textFaint}`} />
                  <span className={`${t.textDim} text-[11px]`}>
                    {pendingImages.length} image{pendingImages.length !== 1 ? "s" : ""} attached
                    {uploadingCount > 0 && ` · uploading ${uploadingCount}...`}
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap max-h-28 overflow-y-auto">
                  {pendingImages.map((img) => (
                    <div key={img.id} className="relative shrink-0 group/img">
                      <img src={img.preview} alt={img.name} className={`w-10 h-10 object-cover rounded-lg border ${isDark ? "border-white/10" : "border-gray-200"}`} />
                      <button onClick={() => removePendingImage(img.id)} className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100 sm:opacity-100 transition-opacity">
                        <X className="w-2.5 h-2.5 text-white" />
                      </button>
                    </div>
                  ))}
                  {uploadingCount > 0 && (
                    <div className="w-10 h-10 rounded-lg border border-dashed border-white/20 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                    </div>
                  )}
                  <button onClick={() => fileInputRef.current?.click()} className={`w-10 h-10 rounded-lg border border-dashed ${isDark ? "border-white/15 text-white/25" : "border-gray-300 text-gray-300"} flex items-center justify-center hover:text-emerald-400 hover:border-emerald-500/30 transition-colors`} title="Add more images" data-testid="ai-add-more-images">
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Input area */}
            {isActiveChat && (
              <div className={`px-3 sm:px-6 py-3 border-t ${isDark ? "border-white/10" : "border-gray-200"} shrink-0`}>
                {savedPrompts.length > 0 && messages.length === 0 && !activeConvId ? null : savedPrompts.length > 0 ? (
                  <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2 scrollbar-none">
                    {savedPrompts.slice(0, 5).map((p) => (
                      <button key={p.id} onClick={() => applyPrompt(p.text)} className={`px-2.5 py-1 rounded-full ${t.card} ${isDark ? "text-white/50" : "text-gray-500"} text-[11px] hover:bg-emerald-600/20 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors whitespace-nowrap shrink-0`} data-testid={`input-prompt-${p.id}`}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                ) : null}
                <div className="flex flex-col gap-2 max-w-3xl mx-auto">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Describe your item, paste details, or drop a screenshot..."
                    rows={3}
                    className={`w-full ${t.input} border rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-emerald-500/40 overflow-y-auto`}
                    data-testid="ai-message-input"
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="ai-image-input" />
                      <button onClick={() => fileInputRef.current?.click()} className={`${t.textDim} hover:text-emerald-400 p-2 transition-colors rounded-lg ${t.cardHover}`} title="Attach image" data-testid="ai-attach-btn">
                        <ImageIcon className="w-5 h-5" />
                      </button>
                      {speech.isSupported && (
                        <button onClick={speech.toggle} className={`p-2 transition-colors rounded-lg ${speech.isListening ? "text-red-400 bg-red-400/10 animate-pulse" : `${t.textDim} hover:text-emerald-400 ${t.cardHover}`}`} title={speech.isListening ? "Stop listening" : "Voice input"} data-testid="ai-voice-btn">
                          {speech.isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                        </button>
                      )}
                      <span className={`${t.textFaint} text-[10px] hidden sm:inline`}>{speech.isListening ? "Listening..." : "or drag & drop"}</span>
                    </div>
                    {isStreaming ? (
                      <button onClick={stopStreaming} className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-500 active:scale-95 transition-all flex items-center gap-2" data-testid="ai-stop-btn">
                        <Square className="w-4 h-4" /> Stop
                      </button>
                    ) : (
                      <button onClick={() => sendMessage()} disabled={!input.trim() && pendingImages.length === 0} className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium disabled:opacity-30 hover:bg-emerald-500 active:scale-95 transition-all flex items-center gap-2" data-testid="ai-send-btn">
                        <Send className="w-4 h-4" /> Send
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Image lightbox */}
        <AnimatePresence>
          {lightboxSrc && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-pointer" onClick={() => setLightboxSrc(null)} data-testid="image-lightbox">
              <motion.img initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.8, opacity: 0 }} src={lightboxSrc} alt="Full size" className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
              <button onClick={() => setLightboxSrc(null)} className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors" data-testid="lightbox-close">
                <X className="w-6 h-6" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ─── Floating widget rendering ───
  return (
    <>
      {/* FAB — hidden in fullPage mode */}
      {!fullPage && (
        <AnimatePresence>
          {!isOpen && (
            <motion.button
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              onClick={() => setIsOpen(true)}
              className="fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full bg-emerald-600 text-white shadow-lg shadow-emerald-900/40 flex items-center justify-center hover:bg-emerald-500 active:scale-95 transition-all"
              data-testid="ai-assistant-fab"
            >
              <Sparkles className="w-6 h-6" />
            </motion.button>
          )}
        </AnimatePresence>
      )}

      {/* Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Click-outside backdrop — hidden in fullPage mode */}
            {!fullPage && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setIsOpen(false)}
                className="fixed inset-0 z-40 bg-black/30"
                data-testid="ai-backdrop"
              />
            )}

            <motion.div
              initial={fullPage ? { opacity: 1 } : { opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={fullPage ? { opacity: 0 } : { opacity: 0, y: 40, scale: 0.95 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={`flex flex-col ${t.panel} overflow-hidden transition-all duration-300 ${
                fullPage
                  ? "w-full h-[calc(100vh-120px)] rounded-xl border"
                  : `fixed z-50 rounded-2xl shadow-2xl border ${
                      isExpanded
                        ? "inset-2 sm:inset-auto sm:top-[2vh] sm:left-0 sm:right-0 sm:mx-auto sm:w-[90vw] sm:max-w-[1200px] sm:h-[96vh]"
                        : "bottom-2 right-2 left-2 sm:left-auto sm:bottom-4 sm:right-4 sm:w-[440px]"
                    }`
              }`}
              style={fullPage || isExpanded ? {} : { maxHeight: "min(85dvh, 640px)" }}
              data-testid="ai-assistant-panel"
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
            {/* Header */}
            <div className={`flex items-center justify-between px-4 py-3 ${t.header} shrink-0`}>
              <div className="flex items-center gap-2">
                {(activeConvId || showHistory || showPrompts) && (
                  <button
                    onClick={() => {
                      if (showHistory || showPrompts) { setShowHistory(false); setShowPrompts(false); }
                      else { setActiveConvId(null); setMessages([]); }
                    }}
                    className={`${t.textMuted} hover:${t.text} p-1`}
                    data-testid="ai-back-btn"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                )}
                <Sparkles className="w-5 h-5 text-emerald-400" />
                <span className={`${t.text} font-semibold text-sm`}>
                  {showHistory ? "Chat History" : showPrompts ? "Saved Prompts" : "Listing Assistant"}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => { setShowPrompts((v) => !v); setShowHistory(false); }}
                  className={`p-1.5 rounded-lg transition-colors ${showPrompts ? "text-emerald-400 bg-emerald-400/10" : t.iconBtn}`}
                  title="Saved prompts"
                  data-testid="ai-prompts-btn"
                >
                  <Bookmark className="w-4 h-4" />
                </button>
                <button
                  onClick={() => { setShowHistory((v) => !v); setShowPrompts(false); }}
                  className={`p-1.5 rounded-lg transition-colors ${showHistory ? "text-emerald-400 bg-emerald-400/10" : t.iconBtn}`}
                  title="Chat history"
                  data-testid="ai-history-btn"
                >
                  <MessageSquare className="w-4 h-4" />
                </button>
                <button onClick={startNewChat} className={`${t.iconBtn} p-1.5 rounded-lg`} title="New chat" data-testid="ai-new-chat-btn">
                  <Plus className="w-4 h-4" />
                </button>
                {!fullPage && (
                  <button
                    onClick={() => setIsExpanded((v) => !v)}
                    className={`hidden sm:flex ${t.iconBtn} p-1.5 rounded-lg`}
                    title={isExpanded ? "Collapse" : "Expand"}
                    data-testid="ai-expand-btn"
                  >
                    {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                  </button>
                )}
                {!fullPage && (
                  <button onClick={() => setIsOpen(false)} className={`${t.iconBtn} p-1.5 rounded-lg`} data-testid="ai-close-btn">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>

            {/* Body — flex row when expanded (sidebar + chat), single col when compact */}
            <div className={`flex-1 flex ${isExpanded ? "flex-row" : "flex-col"} overflow-hidden min-h-0`}>
              {/* Left sidebar — recent conversations (expanded mode only) */}
              {isExpanded && (
                <div className={`w-[220px] shrink-0 border-r ${isDark ? "border-white/10" : "border-gray-200"} overflow-y-auto`} data-testid="ai-sidebar">
                  <div className="p-3">
                    <div className="flex items-center justify-between mb-3">
                      <p className={`${t.textMuted} text-xs font-semibold uppercase tracking-wider`}>Recent</p>
                      <button onClick={startNewChat} className={`${t.iconBtn} p-1 rounded`} title="New chat" data-testid="sidebar-new-chat">
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="space-y-1">
                      {conversations.length === 0 ? (
                        <p className={`${t.textDim} text-xs text-center py-4`}>No chats yet</p>
                      ) : conversations.map((c) => (
                        <div
                          key={c.id}
                          onClick={() => openConversation(c.id)}
                          className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-colors group ${
                            activeConvId === c.id
                              ? isDark ? "bg-emerald-600/20 text-emerald-300" : "bg-emerald-50 text-emerald-700"
                              : `${t.cardHover}`
                          }`}
                          data-testid={`sidebar-conv-${c.id}`}
                        >
                          <MessageSquare className={`w-3.5 h-3.5 shrink-0 ${activeConvId === c.id ? "" : t.textDim}`} />
                          <div className="min-w-0 flex-1">
                            <p className={`text-xs font-medium truncate ${activeConvId === c.id ? "" : t.text}`}>{c.title}</p>
                          </div>
                          <button
                            onClick={(e) => deleteConversation(c.id, e)}
                            className={`${t.textFaint} hover:text-red-400 p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0`}
                            data-testid={`sidebar-delete-${c.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                    {conversations.length > 0 && (
                      <button
                        onClick={async () => {
                          if (!window.confirm("Delete all conversations?")) return;
                          for (const c of conversations) {
                            try { await axios.delete(`${API}/api/ai/conversations/${c.id}`, { headers }); } catch {}
                          }
                          setActiveConvId(null);
                          setMessages([]);
                          loadConversations();
                          toast.success("All chats cleared");
                        }}
                        className={`w-full text-center py-2 mt-3 text-[10px] text-red-400/60 hover:text-red-400 transition-colors`}
                        data-testid="sidebar-clear-all"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
              )}
              {/* Main content area */}
              <div ref={messagesContainerRef} className={`flex-1 overflow-y-auto px-3 py-4 relative ${isExpanded ? "min-w-0" : ""}`}>
                {/* Drag overlay */}
                {isDragging && (
                  <div className={`absolute inset-0 z-10 ${t.dragOverlay} border-2 border-dashed rounded-xl flex flex-col items-center justify-center backdrop-blur-sm`}>
                    <Upload className="w-10 h-10 text-emerald-400 mb-2" />
                    <p className="text-emerald-300 font-medium text-sm">Drop images here</p>
                    <p className="text-emerald-300/60 text-xs mt-1">JPEG, PNG, WEBP up to 5MB</p>
                  </div>
                )}

                {showPrompts ? (
                  /* ---- Saved Prompts Manager ---- */
                  <div className="space-y-3">
                    {isAddingPrompt ? (
                      <div className={`p-3 rounded-xl ${t.card} space-y-2`}>
                        <input
                          value={newPromptLabel}
                          onChange={(e) => setNewPromptLabel(e.target.value)}
                          placeholder="Prompt name (e.g. 'Vintage Denim')"
                          className={`w-full ${t.input} rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-emerald-500/40`}
                          data-testid="new-prompt-label-input"
                        />
                        <textarea
                          value={newPromptText}
                          onChange={(e) => setNewPromptText(e.target.value)}
                          placeholder="Prompt text that will be sent to the assistant..."
                          rows={3}
                          className={`w-full ${t.input} rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:border-emerald-500/40`}
                          data-testid="new-prompt-text-input"
                        />
                        <div className="flex gap-2 justify-end">
                          <button onClick={() => { setIsAddingPrompt(false); setNewPromptLabel(""); setNewPromptText(""); }} className={`px-3 py-1.5 rounded-lg ${t.textDim} text-xs ${t.cardHover}`}>Cancel</button>
                          <button onClick={saveNewPrompt} className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs hover:bg-emerald-500" data-testid="save-new-prompt-btn">Save</button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setIsAddingPrompt(true)}
                        className={`w-full p-3 rounded-xl border border-dashed ${isDark ? "border-white/20 text-white/50" : "border-gray-300 text-gray-400"} text-sm hover:border-emerald-500/40 hover:text-emerald-300 transition-colors flex items-center justify-center gap-2`}
                        data-testid="add-prompt-btn"
                      >
                        <Plus className="w-4 h-4" /> Create New Prompt
                      </button>
                    )}

                    {savedPrompts.length === 0 && !isAddingPrompt && (
                      <p className={`${t.textFaint} text-sm text-center mt-6`}>No saved prompts yet. Create one to use it with a single tap.</p>
                    )}

                    {savedPrompts.map((p) => (
                      <div key={p.id} className={`p-3 rounded-xl ${t.card} ${t.cardHover} transition-colors group`} data-testid={`prompt-item-${p.id}`}>
                        {editingPromptId === p.id ? (
                          <div className="space-y-2">
                            <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className={`w-full ${t.input} rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-emerald-500/40`} data-testid="edit-prompt-label-input" />
                            <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} className={`w-full ${t.input} rounded-lg px-3 py-1.5 text-sm resize-none focus:outline-none focus:border-emerald-500/40`} data-testid="edit-prompt-text-input" />
                            <div className="flex gap-2 justify-end">
                              <button onClick={() => setEditingPromptId(null)} className={`px-2 py-1 ${t.textDim} text-xs ${t.cardHover} rounded`}>Cancel</button>
                              <button onClick={() => updatePrompt(p.id)} className="px-2 py-1 bg-emerald-600 text-white text-xs rounded hover:bg-emerald-500" data-testid="confirm-edit-prompt-btn"><Check className="w-3 h-3" /></button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <button onClick={() => applyPrompt(p.text)} className="text-left flex-1 min-w-0">
                              <p className={`${t.text} font-medium text-sm truncate`}>{p.label}</p>
                              <p className={`${t.textDim} text-xs mt-0.5 line-clamp-2`}>{p.text}</p>
                            </button>
                            <div className="flex items-center gap-0.5 shrink-0">
                              <button onClick={() => { setEditingPromptId(p.id); setEditLabel(p.label); setEditText(p.text); }} className={`p-1 ${t.textFaint} hover:${t.text}`} data-testid={`edit-prompt-${p.id}`}>
                                <Edit3 className="w-3.5 h-3.5" />
                              </button>
                              <button onClick={(e) => deletePrompt(p.id, e)} className={`p-1 ${t.textFaint} hover:text-red-400`} data-testid={`delete-prompt-${p.id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : showHistory && !isExpanded ? (
                  /* ---- Conversation list (compact mode only — expanded shows in sidebar) ---- */
                  <div className="space-y-2">
                    {conversations.length > 0 && (
                      <button
                        onClick={async () => {
                          if (!window.confirm("Delete all conversations?")) return;
                          for (const c of conversations) {
                            try { await axios.delete(`${API}/api/ai/conversations/${c.id}`, { headers }); } catch {}
                          }
                          setActiveConvId(null);
                          setMessages([]);
                          loadConversations();
                          toast.success("All chats cleared");
                        }}
                        className="w-full text-center py-1.5 text-xs text-red-400 hover:text-red-300 transition-colors"
                        data-testid="clear-all-chats-btn"
                      >
                        Clear all chats
                      </button>
                    )}
                    {conversations.length === 0 ? (
                      <p className={`${t.textDim} text-sm text-center mt-8`}>No conversations yet</p>
                    ) : conversations.map((c) => (
                      <div key={c.id} onClick={() => openConversation(c.id)} className={`flex items-center justify-between p-3 rounded-xl ${t.card} ${t.cardHover} cursor-pointer transition-colors group`} data-testid={`conv-item-${c.id}`}>
                        <div className="min-w-0 flex-1">
                          <p className={`${t.text} text-sm font-medium truncate`}>{c.title}</p>
                          <p className={`${t.textDim} text-xs mt-0.5`}>{new Date(c.updated_at).toLocaleDateString()}</p>
                        </div>
                        <button onClick={(e) => deleteConversation(c.id, e)} className={`${t.textFaint} hover:text-red-400 p-1 transition-opacity`} data-testid={`conv-delete-${c.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
                  </div>
                ) : messages.length === 0 && !activeConvId ? (
                  /* ---- Welcome ---- */
                  <div className="flex flex-col items-center justify-center h-full text-center px-4">
                    <div className="w-14 h-14 rounded-full bg-emerald-600/20 flex items-center justify-center mb-4">
                      <Sparkles className="w-7 h-7 text-emerald-400" />
                    </div>
                    <h3 className={`${t.text} font-semibold text-base mb-1`}>Listing Assistant</h3>
                    <p className={`${t.textDim} text-sm mb-5 max-w-[300px]`}>
                      Drop a screenshot or describe an item. I'll write a Vendoo listing for you.
                    </p>

                    {savedPrompts.length > 0 && (
                      <div className="w-full max-w-[320px] mb-4">
                        <p className={`${t.textFaint} text-xs uppercase tracking-wider mb-2`}>Your prompts</p>
                        <div className="flex flex-wrap gap-2 justify-center">
                          {savedPrompts.slice(0, 6).map((p) => (
                            <button
                              key={p.id}
                              onClick={() => applyPrompt(p.text)}
                              className={`px-3 py-1.5 rounded-full ${t.card} ${isDark ? "text-white/70" : "text-gray-600"} text-xs hover:bg-emerald-600/20 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors truncate max-w-[140px]`}
                              data-testid={`welcome-prompt-${p.id}`}
                            >
                              {p.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className={`w-full max-w-[300px] p-4 rounded-xl border border-dashed ${isDark ? "border-white/15 text-white/30" : "border-gray-300 text-gray-400"} text-xs flex flex-col items-center gap-1.5`}>
                      <Upload className={`w-5 h-5 ${isDark ? "text-white/20" : "text-gray-300"}`} />
                      Drag & drop screenshots here
                    </div>
                  </div>
                ) : (
                  <>
                    {messages.map(renderMessage)}
                    {/* Regenerate / Delete last exchange buttons */}
                    {!isStreaming && messages.length >= 2 && messages[messages.length - 1]?.role === "assistant" && (
                      <div className="flex items-center gap-2 justify-center py-1">
                        <button
                          onClick={regenerateLastMessage}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${t.textDim} ${t.cardHover} transition-colors`}
                          title="Regenerate response"
                          data-testid="ai-regenerate-btn"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                          Retry
                        </button>
                        <button
                          onClick={deleteLastExchange}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs ${t.textDim} hover:text-red-400 transition-colors`}
                          title="Delete last exchange"
                          data-testid="ai-delete-last-btn"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Delete
                        </button>
                      </div>
                    )}
                    <div ref={messagesEndRef} />
                  </>
                )}
              </div>

            </div>

            {/* Pending images strip */}
            {(pendingImages.length > 0 || uploadingCount > 0) && (
              <div className={`px-3 py-2 border-t ${isDark ? "border-white/5" : "border-gray-200"} shrink-0`}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <ImageIcon className={`w-3.5 h-3.5 ${t.textFaint}`} />
                  <span className={`${t.textDim} text-[11px]`}>
                    {pendingImages.length} image{pendingImages.length !== 1 ? "s" : ""} attached
                    {uploadingCount > 0 && ` · uploading ${uploadingCount}...`}
                  </span>
                </div>
                <div className="flex gap-2 flex-wrap max-h-28 overflow-y-auto">
                  {pendingImages.map((img) => (
                    <div key={img.id} className="relative shrink-0 group/img">
                      <img src={img.preview} alt={img.name} className={`w-10 h-10 object-cover rounded-lg border ${isDark ? "border-white/10" : "border-gray-200"}`} />
                      <button
                        onClick={() => removePendingImage(img.id)}
                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100 sm:opacity-100 transition-opacity"
                      >
                        <X className="w-2.5 h-2.5 text-white" />
                      </button>
                    </div>
                  ))}
                  {uploadingCount > 0 && (
                    <div className="w-10 h-10 rounded-lg border border-dashed border-white/20 flex items-center justify-center">
                      <Loader2 className="w-4 h-4 text-emerald-400 animate-spin" />
                    </div>
                  )}
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className={`w-10 h-10 rounded-lg border border-dashed ${isDark ? "border-white/15 text-white/25" : "border-gray-300 text-gray-300"} flex items-center justify-center hover:text-emerald-400 hover:border-emerald-500/30 transition-colors`}
                    title="Add more images"
                    data-testid="ai-add-more-images"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {/* Input area — large box */}
            {isActiveChat && (
              <div className={`px-3 py-3 border-t ${t.header} shrink-0`}>
                {/* Saved prompt pills above input */}
                {savedPrompts.length > 0 && messages.length === 0 && !activeConvId ? null : savedPrompts.length > 0 ? (
                  <div className="flex gap-1.5 overflow-x-auto pb-2 mb-2 scrollbar-none">
                    {savedPrompts.slice(0, 5).map((p) => (
                      <button
                        key={p.id}
                        onClick={() => applyPrompt(p.text)}
                        className={`px-2.5 py-1 rounded-full ${t.card} ${isDark ? "text-white/50" : "text-gray-500"} text-[11px] hover:bg-emerald-600/20 hover:text-emerald-300 hover:border-emerald-500/30 transition-colors whitespace-nowrap shrink-0`}
                        data-testid={`input-prompt-${p.id}`}
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                ) : null}

                <div className="flex flex-col gap-2">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Describe your item, paste details, or drop a screenshot..."
                    rows={4}
                    className={`w-full ${t.input} border rounded-xl px-4 py-3 text-sm resize-none focus:outline-none focus:border-emerald-500/40 overflow-y-auto`}
                    data-testid="ai-message-input"
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/jpeg,image/png,image/webp" multiple className="hidden" data-testid="ai-image-input" />
                      <button onClick={() => fileInputRef.current?.click()} className={`${t.textDim} hover:text-emerald-400 p-2 transition-colors rounded-lg ${t.cardHover}`} title="Attach image" data-testid="ai-attach-btn">
                        <ImageIcon className="w-5 h-5" />
                      </button>
                      {speech.isSupported && (
                        <button
                          onClick={speech.toggle}
                          className={`p-2 transition-colors rounded-lg ${speech.isListening ? "text-red-400 bg-red-400/10 animate-pulse" : `${t.textDim} hover:text-emerald-400 ${t.cardHover}`}`}
                          title={speech.isListening ? "Stop listening" : "Voice input"}
                          data-testid="ai-voice-btn"
                        >
                          {speech.isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                        </button>
                      )}
                      <span className={`${t.textFaint} text-[10px] hidden sm:inline`}>{speech.isListening ? "Listening..." : "or drag & drop"}</span>
                    </div>
                    {isStreaming ? (
                      <button
                        onClick={stopStreaming}
                        className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-medium hover:bg-red-500 active:scale-95 transition-all flex items-center gap-2"
                        data-testid="ai-stop-btn"
                      >
                        <Square className="w-4 h-4" />
                        Stop
                      </button>
                    ) : (
                      <button
                        onClick={() => sendMessage()}
                        disabled={!input.trim() && pendingImages.length === 0}
                        className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium disabled:opacity-30 hover:bg-emerald-500 active:scale-95 transition-all flex items-center gap-2"
                        data-testid="ai-send-btn"
                      >
                        <Send className="w-4 h-4" />
                        Send
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Image lightbox */}
      <AnimatePresence>
        {lightboxSrc && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4 cursor-pointer"
            onClick={() => setLightboxSrc(null)}
            data-testid="image-lightbox"
          >
            <motion.img
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              src={lightboxSrc}
              alt="Full size"
              className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setLightboxSrc(null)}
              className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full bg-black/40 hover:bg-black/60 transition-colors"
              data-testid="lightbox-close"
            >
              <X className="w-6 h-6" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
